import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Box, Typography, CircularProgress, Grid, Button, Drawer, ButtonBase } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import Product from '../components/Product';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import FilterListIcon from '@mui/icons-material/FilterList';

const FilterDrawer = ({ open, onClose, categories, prices, colors, selectedCategories, selectedPrices, selectedColors, onCategorySelect, onPriceSelect, onColorSelect, onApply }) => {
  const theme = useTheme();

  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      sx={{
        '& .MuiDrawer-paper': {
          background: 'transparent',
          boxShadow: 'none'
        }
      }}
    >
      <Box
        sx={{
          width: '100vw',
          height: '100vh',
          display: 'flex',
          alignItems: 'end',
          justifyContent: 'center',
          p: 2
        }}
        onClick={onClose}
      >
        <Box
          onClick={(e) => e.stopPropagation()}
          sx={{
            width: '90%',
            maxWidth: '600px',
            bgcolor: 'background.paper',
            borderRadius: '25px',
            position: 'fixed',
            pb: 4,
            maxHeight: '90vh',
            overflowY: 'auto',
            '&:before': {
              content: '""',
              position: 'absolute',
              top: 8,
              left: '50%',
              transform: 'translateX(-50%)',
              width: 48,
              height: 4,
              backgroundColor: 'grey.300',
              borderRadius: 2
            }
          }}
        >
          <Box sx={{ mt: 3, p: 3 }}>
            <Typography variant="h6" sx={{ mb: 3, textAlign: 'center' }}>
              Filter Products
            </Typography>

            {/* Categories */}
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Categories
            </Typography>
            <Grid container spacing={1} sx={{ mb: 3 }}>
              {categories.map((category) => (
                <Grid item xs={6} key={category.id}>
                  <ButtonBase
                    onClick={() => onCategorySelect(category.id)}
                    sx={{
                      width: '100%',
                      p: 1.5,
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: selectedCategories.includes(category.id) ? 'primary.main' : 'grey.300',
                      backgroundColor: selectedCategories.includes(category.id) ? 'primary.lighter' : 'transparent',
                      textAlign: 'center',
                      transition: 'all 0.2s',
                    }}
                  >
                    <Typography variant="body2" noWrap>
                      {category.name}
                    </Typography>
                  </ButtonBase>
                </Grid>
              ))}
            </Grid>

            {/* Price Ranges */}
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Price Range
            </Typography>
            <Grid container spacing={1} sx={{ mb: 3 }}>
              {prices.map((range) => (
                <Grid item xs={6} key={range.id}>
                  <ButtonBase
                    onClick={() => onPriceSelect(range.id)}
                    sx={{
                      width: '100%',
                      p: 1.5,
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: selectedPrices.includes(range.id) ? 'primary.main' : 'grey.300',
                      backgroundColor: selectedPrices.includes(range.id) ? 'primary.lighter' : 'transparent',
                      textAlign: 'center',
                      transition: 'all 0.2s',
                    }}
                  >
                    <Typography variant="body2" noWrap>
                      {range.name}
                    </Typography>
                  </ButtonBase>
                </Grid>
              ))}
            </Grid>

            {/* Colors */}
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Colors
            </Typography>
            <Grid container spacing={1} sx={{ mb: 3 }}>
              {colors.map((color) => (
                <Grid item xs={4} key={color.id}>
                  <ButtonBase
                    onClick={() => onColorSelect(color.id)}
                    sx={{
                      width: '100%',
                      p: 1.5,
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: selectedColors.includes(color.id) ? 'primary.main' : 'grey.300',
                      backgroundColor: selectedColors.includes(color.id) ? 'primary.lighter' : 'transparent',
                      textAlign: 'center',
                      transition: 'all 0.2s',
                    }}
                  >
                    <Typography variant="body2" noWrap>
                      {color.name}
                    </Typography>
                  </ButtonBase>
                </Grid>
              ))}
            </Grid>

            <Button
              variant="contained"
              onClick={onApply}
              sx={{
                mt: 2,
                py: 1.5,
                borderRadius: 2,
                textTransform: 'none',
                fontSize: '1.1rem',
                width: '100%'
              }}
            >
              Apply Filters
            </Button>
          </Box>
        </Box>
      </Box>
    </Drawer>
  );
};

const Search = () => {
  const { user, api } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = location.state || {};
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const observer = useRef();
  const theme = useTheme();

  // Filter states
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState(searchParams.categories || []);
  const [selectedPrices, setSelectedPrices] = useState(searchParams.prices || []);
  const [selectedColors, setSelectedColors] = useState(searchParams.colors || []);

  const categories = user?.gender?.toLowerCase() === 'women' 
    ? [
        { id: 'all', name: 'All' },
        { id: 'dresses', name: 'Dresses' },
        { id: 'tops', name: 'Tops' },
        { id: 'pants', name: 'Pants' },
        { id: 'skirts', name: 'Skirts' },
        { id: 'outerwear', name: 'Outerwear' },
        { id: 'shoes', name: 'Shoes' },
        { id: 'bags', name: 'Bags' },
        { id: 'accessories', name: 'Accessories' },
        { id: 'swimwear', name: 'Swimwear' },
      ]
    : [
        { id: 'all', name: 'All' },
        { id: 'tops', name: 'Tops' },
        { id: 'pants', name: 'Pants' },
        { id: 'outerwear', name: 'Outerwear' },
        { id: 'suits', name: 'Suits' },
        { id: 'shoes', name: 'Shoes' },
        { id: 'accessories', name: 'Accessories' },
        { id: 'underwear', name: 'Underwear' },
        { id: 'swimwear', name: 'Swimwear' },
        { id: 'any', name: 'Nothing specific' },
      ];

  const priceRanges = [
    { id: 'under50', name: 'Under $50' },
    { id: '50to100', name: '$50 - $100' },
    { id: '100to200', name: '$100 - $200' },
    { id: 'over200', name: 'Over $200' },
    { id: 'noLimit', name: 'Too rich to care' },
  ];

  const colors = [
    { id: 'black', name: 'Black' },
    { id: 'white', name: 'White' },
    { id: 'blue', name: 'Blue' },
    { id: 'red', name: 'Red' },
    { id: 'green', name: 'Green' },
    { id: 'yellow', name: 'Yellow' },
    { id: 'pink', name: 'Pink' },
    { id: 'purple', name: 'Purple' },
    { id: 'brown', name: 'Brown' },
    { id: 'gray', name: 'Gray' },
    { id: 'any', name: 'Any color' },
  ];

  const handleCategorySelect = (categoryId) => {
    setSelectedCategories(prev => {
      if (categoryId === 'all' || categoryId === 'any') {
        return [categoryId];
      }
      if (prev.includes('all') || prev.includes('any')) {
        return [categoryId];
      }
      const newSelection = prev.includes(categoryId)
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId];
      return newSelection.length === 0 ? [] : newSelection;
    });
  };

  const handlePriceSelect = (priceId) => {
    setSelectedPrices(prev => {
      if (priceId === 'noLimit') {
        return ['noLimit'];
      }
      if (prev.includes('noLimit')) {
        return [priceId];
      }
      const newSelection = prev.includes(priceId)
        ? prev.filter(id => id !== priceId)
        : [...prev, priceId];
      return newSelection.length === 0 ? [] : newSelection;
    });
  };

  const handleColorSelect = (colorId) => {
    setSelectedColors(prev => {
      if (colorId === 'any') {
        return ['any'];
      }
      if (prev.includes('any')) {
        return [colorId];
      }
      const newSelection = prev.includes(colorId)
        ? prev.filter(id => id !== colorId)
        : [...prev, colorId];
      return newSelection.length === 0 ? [] : newSelection;
    });
  };

  const handleApplyFilters = () => {
    setIsFilterOpen(false);
    setPage(1);
    setProducts([]);
    navigate('/search', {
      state: {
        categories: selectedCategories,
        prices: selectedPrices,
        colors: selectedColors
      },
      replace: true
    });
  };

  // Validate search parameters
  useEffect(() => {
    console.log('Location state:', location.state);
    if (!location.state || 
        (!location.state.categories?.length && 
         !location.state.prices?.length && 
         !location.state.colors?.length)) {
      console.log('No search parameters found, redirecting to home');
      navigate('/', { replace: true });
    }
  }, [location.state, navigate]);

  const lastProductElementRef = useCallback(node => {
    if (loading) return;
    if (observer.current) observer.current.disconnect();
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore) {
        setPage(prevPage => prevPage + 1);
      }
    });
    if (node) observer.current.observe(node);
  }, [loading, hasMore]);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Validate search parameters
        if (!searchParams.categories && !searchParams.prices && !searchParams.colors) {
          console.log('No search parameters available');
          setError('No search parameters available');
          setLoading(false);
          return;
        }

        const searchRequest = {
          categories: searchParams.categories || [],
          prices: searchParams.prices || [],
          colors: searchParams.colors || [],
          page: page
        };

        console.log('Sending search request with params:', searchRequest);

        const response = await api.post('/api/products/search', searchRequest);

        console.log('Search response:', response.data);

        const newProducts = response.data.products || [];
        setHasMore(newProducts.length > 0);
        setProducts(prevProducts => {
          if (page === 1) return newProducts;
          return [...prevProducts, ...newProducts];
        });
      } catch (err) {
        console.error('Error fetching search results:', err);
        console.error('Error details:', err.response?.data || err.message);
        setError('Failed to load search results. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    if (user && searchParams && Object.keys(searchParams).length > 0) {
      console.log('Fetching products with search params:', searchParams);
      fetchProducts();
    }
  }, [user, api, page, searchParams]);

  // Create search description text
  const getSearchDescription = () => {
    const parts = [];
    
    // Add categories
    if (searchParams.categories?.includes('all')) {
      parts.push('all categories');
    } else if (searchParams.categories?.includes('any')) {
      // Don't add category description if "any" is selected
    } else if (searchParams.categories?.length > 0) {
      parts.push(searchParams.categories.join(', '));
    }

    // Add price range
    if (searchParams.prices?.includes('noLimit')) {
      parts.push('any price');
    } else if (searchParams.prices?.length > 0) {
      const priceDesc = searchParams.prices.map(price => {
        switch(price) {
          case 'under50': return 'under $50';
          case '50to100': return '$50-$100';
          case '100to200': return '$100-$200';
          case 'over200': return 'over $200';
          default: return '';
        }
      }).filter(Boolean).join(', ');
      if (priceDesc) parts.push(priceDesc);
    }

    // Add colors
    if (searchParams.colors?.includes('any')) {
      parts.push('any color');
    } else if (searchParams.colors?.length > 0) {
      parts.push(searchParams.colors.join(', '));
    }

    return parts.join(' • ');
  };

  return (
    <Box sx={{ mt: 10, px: 3, maxWidth: 600, mx: 'auto', pb: 8 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4" sx={{ fontWeight: 600 }}>
          Search Results
        </Typography>
        <Button
          variant="outlined"
          startIcon={<FilterListIcon />}
          onClick={() => setIsFilterOpen(true)}
          sx={{
            borderRadius: 2,
            textTransform: 'none'
          }}
        >
          Filter
        </Button>
      </Box>
      
      <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
        {getSearchDescription()}
      </Typography>

      <FilterDrawer
        open={isFilterOpen}
        onClose={() => setIsFilterOpen(false)}
        categories={categories}
        prices={priceRanges}
        colors={colors}
        selectedCategories={selectedCategories}
        selectedPrices={selectedPrices}
        selectedColors={selectedColors}
        onCategorySelect={handleCategorySelect}
        onPriceSelect={handlePriceSelect}
        onColorSelect={handleColorSelect}
        onApply={handleApplyFilters}
      />

      {loading && page === 1 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Typography color="error" sx={{ mt: 6, textAlign: 'center' }}>
          {error}
        </Typography>
      ) : (
        <Grid container spacing={2}>
          {products.map((product, index) => {
            // Find primary image or use first available image
            const images = product.media?.images || [];
            const primaryImage = images.find(img => img.isPrimary)?.url;
            const firstImage = images[0]?.url;
            const imageUrl = primaryImage || firstImage;

            return (
              <Grid 
                item 
                xs={6} 
                key={product.id}
                ref={index === products.length - 1 ? lastProductElementRef : null}
              >
                <Product
                  id={product.id}
                  image={imageUrl}
                  brand={product.brand?.name}
                  name={product.title}
                  price={product.price}
                  originalPrice={product.original_price}
                  productDetails={product.product_details}
                  variants={product.variants}
                  category={product.category}
                  color={product.variants?.[0]?.color}
                  product={product}
                />
              </Grid>
            );
          })}
        </Grid>
      )}
    </Box>
  );
};

export default Search; 